<i18n>
{
  "en": {
    "initialRecommendations": "Enable initial recommendation",
    "initialRecommendationsOptions": "Initial recommendation settings",
    "initialRecommendationText": "Initial recommendation settings",
    "initialRecommendationImage": "Top image for initial recommendation",
    "changeInitialRecommendationImage": "Select another initial recommendation image",
    "defaultInitialRecommendationTitle": "Default initial recommendations title",
    "defaultInitialRecommendationText": "Default initial recommendations",
    "customInitialRecommendationText": "Custom initial recommendations",
    "createCustomInitialRecommendation": "Create custom initial recommendations",
    "addInitialRecommendation": "Add default initial recommendations",
    "deleteInitialRecommendation": "Delete initial recommendation",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "typeToSearch": "Type to search",
    "tooLongText": "Input text is too long",
    "initialRecommendationTitleText": "Default recommendation",
    "notInitialRecommendationTitleText": "There are not default initial recommendations created",
    "selectAProduct": "Select a product",
    "emptyFieldError": "Mandatory field",
    "fileFormatError": "This format is not valid"
  },
  "es": {
    "initialRecommendations": "Activar la recomendación inicial",
    "initialRecommendationsOptions": "Personalización de la recomendación inicial",
    "initialRecommendationTitle": "Título de la recomendación inicial",
    "initialRecommendationText": "Texto de recomendación inicial",
    "initialRecommendationImage": "Imagen superior para la recomendación inicial",
    "changeInitialRecommendationImage": "Selecciona otra imagen para la recomendación inicial",
    "defaultInitialRecommendationText": "Recomendaciones iniciales por defecto",
    "customInitialRecommendationText": "Recomendaciones iniciales personalizadas",
    "createCustomInitialRecommendation": "Crear más recomendaciones",
    "addInitialRecommendation": "Añadir recomendaciones iniciales por defecto",
    "deleteInitialRecommendation": "Borrar recomendación inicial",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "typeToSearch": "Escribe para buscar",
    "tooLongText": "El texto es demasiado largo",
    "initialRecommendationTitleText": "Recomendación por defecto",
    "notInitialRecommendationTitleText": "No hay recomendaciones por defecto creadas",
    "selectAProduct": "Selecciona un producto",
    "emptyFieldError": "Campo obligatorio",
    "fileFormatError": "Este formato no es valido"
  }
}
</i18n>

<template>
  <div v-if="initialRecommendation">
    <div class="d-flex flex-row justify-space-between mt-4">
      <div class="text-subtitle-2 mb-2">{{ $t('initialRecommendationTitleText') }}</div>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="ma-0 pa-0 align-self-top" v-on="on" v-bind="attrs"
                 @click="onDelete(initialRecommendation)">
            <v-icon color="grey" class="mb-4">mdi-delete</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('deleteInitialRecommendation') }}</span>
      </v-tooltip>
    </div>
    <dynameat-language-field-generator v-model="text" :label="$t('initialRecommendationsOptions')"
                                       :type="'textField'" :rules="initialRecommendationTextRules"
                                       @input="handleChange"/>
    <dynameat-autocomplete v-model="product"  :items="availableProductsList"
                  :filter="productTextFilter" :placeholder="$t('typeToSearch')"
                  prepend-icon="mdi-database-search" item-text="name" chips persistent-hint
                  item-value="guid" :label="$t('selectAProduct')" @change="onChange">
      <template v-slot:item="data">
        <template>
          <v-list-item-avatar>
            <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg" contain/>
          </v-list-item-avatar>
          <v-list-item-content class="h-scroll">
            <v-list-item-title class="no-wrap">
              {{ data.item.commercial_name }} ({{ data.item.first_price }}€)
            </v-list-item-title>
            <v-list-item-subtitle class="no-wrap">
              {{ data.item.name }} ({{ data.item.id_external }})
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </template>
      <template v-slot:selection="data">
        <v-chip class="caption | d-flex justify-center align-center"
                close @click:close="onRemoveRecommendedProduct(initialRecommendation)">
          <div class="chip-ellipsis">{{ data.item.name }}</div>
        </v-chip>
      </template>
    </dynameat-autocomplete>

    <v-file-input v-model="image" @change="handleChange" :rules="imageRules" show-size
                accept="image/png, image/jpeg, image/bmp" prepend-icon="mdi-camera"
                :label="$t('changeInitialRecommendationImage')" class="ma-0 pa-0"/>
    <!-- Master Product modal -->
    <master-product-modal :visible="masterProductModalVisible" :product-prop="masterProductToEdit"
                          @close="onEditMasterProduct"/>
    <v-divider class="mb-5"/>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop } from 'vue-property-decorator';
import { MenuInitialRecommendation } from '@manualConfig/views/menuInitialRecommendation/models';
import { MapGetters, Components, Mixins } from '@core/helpers/decorators';
import cloneDeep from 'lodash/cloneDeep';
import { imageFileType, maxChars, maxSize } from '@core/helpers/formValidators';
import { productTextFilter } from '@core/mixins';
import { productMixins } from '@pointOfSale/mixins';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import DynameatLanguageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import MasterProductModal from '@masters/views/product/components/MasterProductModal.vue';

@Component
@Components({
  DynameatAutocomplete,
  DynameatLanguageFieldGenerator,
  MasterProductModal
})
@Mixins([productMixins])
@MapGetters(['pointOfSaleLanguages'])
export default class MenuInitialRecommendationSettings extends Vue {
  @Prop({
    Object,
    validator(initialRecommendation) {
      return initialRecommendation instanceof MenuInitialRecommendation;
    },
  }) initialRecommendationProp;

  @Prop({ type: Function, default: () => {} }) onDelete;

  @Prop({ type: String, default: '' }) defaultRecommendationTitle;

  initialRecommendation = null;

  title = {};

  text = {};

  product = null;

  image = null;

  masterProductModalVisible = false;

  masterProductToEdit = null;

  productTextFilter = productTextFilter

  created() {
    this.initialRecommendation = cloneDeep(this.initialRecommendationProp);
    this.loadInitialRecommendationChanges();
  }

  updated() {
    this.applyInitialRecommendationChanges();
    this.$emit('onChange', this.initialRecommendation);
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get defaultProductImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get initialRecommendationTextRules() {
    return {
      alt1: [maxChars(this.$t('tooLongText'), 128)],
      alt2: [maxChars(this.$t('tooLongText'), 128)],
      alt3: [maxChars(this.$t('tooLongText'), 128)]
    };
  }

  get emptyMainText() {
    let emptyText = true;
    if (this.text.alt1) {
      emptyText = this.text.alt1.trim() === '';
    }
    return emptyText;
  }

  applyInitialRecommendationChanges() {
    this.initialRecommendation.title = this.emptyMainText ? this.defaultRecommendationTitle : this.text.alt1;
    this.pointOfSaleLanguages.forEach((el) => {
      this.initialRecommendation[`text${el.alternative.charAt(0).toUpperCase() + el.alternative.slice(1)}`] = this.text[el.alternative];
    });
    this.initialRecommendation.product = this.product;
    this.initialRecommendation.image = this.image;
    this.$emit('onChange', this.initialRecommendation);
  }

  handleChange() {
    this.applyInitialRecommendationChanges();
  }

  onChange(event) {
    const productFined = this.availableProductsList.filter((product) => product.guid === event);
    if (productFined.length > 0) {
      const product = productFined[0];
      if (product.is_new || !product.isConfigured) {
        this.masterProductToEdit = product;
        this.masterProductModalVisible = true;
      }
    }
  }

  onEditMasterProduct(product) {
    if (!product) {
      this.product = null;
    }
    this.masterProductModalVisible = false;
    this.masterProductToEdit = null;
  }

  loadInitialRecommendationChanges() {
    this.title = this.initialRecommendation.title;
    this.pointOfSaleLanguages.forEach((el) => {
      this.text[el.alternative] = this.initialRecommendation[`text${el.alternative.charAt(0).toUpperCase() + el.alternative.slice(1)}`];
    });
    this.product = this.initialRecommendation.product;
    this.image = this.initialRecommendation.image;
  }

  onRemoveRecommendedProduct(initialRecommendation) {
    initialRecommendation.product = null;
    this.product = null;
  }
}
</script>
<style scoped>
.required label::after {
  content: " *";
  color: red;
}
</style>
